import "@/styles/globals.css";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { appWithTranslation } from "next-i18next";
import { BRANCH_COUNT } from "@/constants/branches";
import nextI18NextConfig from "../next-i18next.config";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { captureAdAttribution } from "@/utils/adAttribution";

// Fonts - optimized with display swap
import { Inter } from "next/font/google";
// Swiper styles
import "swiper/css";
// Redux - required for state management (but defer persist)
import store from "@/store/store";
import { Provider } from "react-redux";

// React Query - required for data fetching
import { QueryClient, QueryClientProvider } from "react-query";

// Critical layout - load immediately
import Layout from "@/layout";

// Dynamically import non-critical components
const Globals = dynamic(() => import("@/globals"), { ssr: false });
const TimeDifference = dynamic(() => import("@/components/timeDifference"), {
  ssr: false,
});
const MainHeadWithTitle = dynamic(() => import("@/components/mainHead"), {
  ssr: false,
});
const SafeArea = dynamic(() => import("@/components/safeArea"), {
  ssr: false,
});

// Toast - defer loading, only needed for interactions
const Toaster = dynamic(
  () => import("react-hot-toast").then((mod) => mod.Toaster),
  { ssr: false }
);

// AntdConfig - defer to reduce initial JS
const AntdConfig = dynamic(() => import("@/utils/antdConfig"), {
  ssr: false,
});

// Create QueryClient ONCE with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

export const inter = Inter({
  subsets: ["latin", "latin-ext"],
  variable: "--font-inter",
  display: "swap",
  preload: true,
});

// Wrapper that shows content immediately, then adds persist later
function AppContent({ Component, pageProps }: AppProps) {
  const [persistReady, setPersistReady] = useState(false);
  const router = useRouter();

  // Reklamadan kelgan fbclid / utm_* ni lid yuborilguncha saqlab qo'yamiz
  useEffect(() => {
    captureAdAttribution();
    router.events.on("routeChangeComplete", captureAdAttribution);
    return () => router.events.off("routeChangeComplete", captureAdAttribution);
  }, [router.events]);

  useEffect(() => {
    // Defer redux-persist setup to not block initial render
    import("@/store/store").then(({ persistor }) => {
      import("redux-persist/integration/react").then(({ PersistGate }) => {
        persistor.persist();
        setPersistReady(true);
      });
    });

    // Defer error handling setup
    import("@/utils/errorHandler").then(({ setupGlobalErrorHandling }) => {
      setupGlobalErrorHandling();
    });
  }, []);

  return (
    <AntdConfig>
      <SafeArea>
        <Toaster position="top-center" />
        <Layout>
          <Component {...pageProps} />
          <TimeDifference />
          <Globals />
        </Layout>
      </SafeArea>
    </AntdConfig>
  );
}

function App(props: AppProps) {
  return (
    <div className={inter.variable}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <MainHeadWithTitle />
          <AppContent {...props} />
        </Provider>
      </QueryClientProvider>
    </div>
  );
}

// {{branchCount}} in locale files comes from the branches list (single source)
export default appWithTranslation(App, {
  ...nextI18NextConfig,
  interpolation: { defaultVariables: { branchCount: BRANCH_COUNT } },
} as any);
